import { useCallback, useEffect, useRef, useState } from 'react'
import { v4 as uuid4 } from 'uuid'

import { BuilderPage } from '@makyoapp/makyo-common'

import { fromSdkPage, toSdkPage } from '@/adapters/__project__/BuilderPageAdapter'
import { useAppClientContext } from '@/context/__project__/AppClientContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { DEFAULT_COMPONENTS_GROUPS } from '@/shared/builder/components'
import { replaceTableIdsRecursive } from '@/shared/collect-table-ids'
import { EVENT_KEYS } from '@/shared/custom-event'
import { genDetailsPage, genFormAddDetailsPage, genFormDetailsPage } from '@/utils/builder/app-builder'

import { useIsProjectDb } from './useIsProjectDb'

interface ProgressStatus {
  current: number
  total: number
  status: 'idle' | 'processing' | 'finished' | 'error'
  errors: number
}

export interface SyncDetailPagesState {
  status: 'idle' | 'processing' | 'finished' | 'error'
  message: string
  addStatus: ProgressStatus
  deleteStatus: ProgressStatus
  totalErrors: number
}

const initialState: SyncDetailPagesState = {
  status: 'idle',
  message: '',
  addStatus: {
    current: 0,
    total: 0,
    status: 'idle',
    errors: 0,
  },
  deleteStatus: {
    current: 0,
    total: 0,
    status: 'idle',
    errors: 0,
  },
  totalErrors: 0,
}

// temporary flag
const ENABLED = true

/**
 * Custom hook to manage details page sync in app builder,
 *
 * @note only available in app builder (window.appMode === 'preview')
 */
export const useSyncDetailPages = () => {
  // global context
  const { appClient } = useAppClientContext()
  const { dbClient, dbWorkerStatus, tables } = useDbClientContext()
  const { pages } = useAppBuilderContext()

  // check if current route is project db
  const isProjectDbRoute = useIsProjectDb()

  // refs
  const isProcessingDetailsPageRef = useRef(false)
  const isProcessingTableActionRef = useRef(false)

  // state
  const [appPreviewLoaded, setAppPreviewLoaded] = useState(false)
  const [syncState, setSyncState] = useState<SyncDetailPagesState>(initialState)

  const handleReset = useCallback(() => {
    setSyncState(initialState)
  }, [])

  const silentlyRunTask = useCallback(
    async (callback: () => void) => {
      if (isProjectDbRoute) return
      callback()
    },
    [isProjectDbRoute]
  )

  const handleGenerateDetailsPage = useCallback(
    async (pagesToAdd: BuilderPage[], pagesToDelete: string[] = []) => {
      if (!appClient) return

      if (pagesToAdd.length === 0 && pagesToDelete.length === 0) return

      const totalPages = pagesToAdd.length + pagesToDelete.length
      let deleteErrors = 0
      let addErrors = 0

      silentlyRunTask(() => {
        setSyncState({
          status: 'processing',
          message: `Processing ${totalPages} pages`,
          deleteStatus: {
            current: 0,
            total: pagesToDelete.length,
            status: 'idle',
            errors: 0,
          },
          addStatus: {
            current: 0,
            total: pagesToAdd.length,
            status: 'idle',
            errors: 0,
          },
          totalErrors: 0,
        })
      })

      for (const [index, pageToDeleteId] of pagesToDelete.entries()) {
        try {
          await appClient.DeletePage(pageToDeleteId, false)

          silentlyRunTask(() => {
            const isLast = index + 1 === pagesToDelete.length
            setSyncState((prev) => ({
              ...prev,
              deleteStatus: {
                current: index + 1,
                total: pagesToDelete.length,
                status: isLast ? 'finished' : 'processing',
                errors: deleteErrors,
              },
            }))
          })
        } catch (error) {
          silentlyRunTask(() => {
            deleteErrors += 1
            setSyncState((prev) => ({
              ...prev,
              deleteStatus: {
                current: index + 1,
                total: pagesToDelete.length,
                status: 'error',
                errors: deleteErrors,
              },
            }))
          })
        }
      }

      for (const [index, pageToAdd] of pagesToAdd.entries()) {
        try {
          await appClient.AddPage(toSdkPage(pageToAdd), false)

          silentlyRunTask(() => {
            const isLast = index + 1 === pagesToAdd.length
            setSyncState((prev) => ({
              ...prev,
              addStatus: {
                current: index + 1,
                total: pagesToAdd.length,
                status: isLast ? 'finished' : 'processing',
                errors: addErrors,
              },
            }))
          })
        } catch (error) {
          silentlyRunTask(() => {
            addErrors += 1
            setSyncState((prev) => ({
              ...prev,
              addStatus: {
                current: index + 1,
                total: pagesToAdd.length,
                status: 'error',
                errors: addErrors,
              },
            }))
          })
        }
      }

      const totalErrors = deleteErrors + addErrors

      appClient.notifySubscribers('page')

      silentlyRunTask(() => {
        setSyncState((prev) => ({
          ...prev,
          status: totalErrors > 0 ? 'error' : 'finished',
          message:
            totalErrors > 0
              ? `Completed with ${totalErrors} ${totalErrors === 1 ? 'error' : 'errors'}`
              : `Successfully processed ${totalPages} ${totalPages === 1 ? 'page' : 'pages'}`,
          totalErrors,
        }))
      })
    },
    [appClient, silentlyRunTask]
  )

  /**
   * Sync details page processing:
   * - delete obsolete pages
   * - add new pages
   */
  const handleStartAutoSync = useCallback(async () => {
    if (!appClient || !tables.length || !pages.length) return

    // track already processed tables to prevent duplicate requests
    if (isProcessingDetailsPageRef.current || isProcessingTableActionRef.current) return

    // mark as processing
    isProcessingDetailsPageRef.current = true

    // Create a Set of valid table IDs for O(1) lookup
    const validTableIds = new Set(tables.map((t) => t.id))

    // collect pages to delete
    const pagesToDelete = pages.filter((p) => !!p.tableId && !validTableIds.has(p.tableId)).map((p) => p.id)

    // Create index maps for O(1) page lookups by tableId and nature
    // Map structure: tableId -> Set of nature keys
    const pageIndex = new Map<string, Set<string>>()

    for (const page of pages) {
      if (!page.tableId) continue

      if (!pageIndex.has(page.tableId)) {
        pageIndex.set(page.tableId, new Set())
      }

      const natureSet = pageIndex.get(page.tableId)!

      // Create unique keys for each page type
      if (page.nature === '') {
        natureSet.add('details')
      } else if (page.nature === 'form' && page.title.startsWith('form-detail')) {
        natureSet.add('form-detail')
      } else if (page.nature === 'form' && page.title.startsWith('form-add')) {
        natureSet.add('form-add')
      }
    }

    // Generate details page based on tables (if not exist yet)
    const pagesToAdd: BuilderPage[] = []

    // Each table needs to have 3 detail pages:
    // (1) details page, (2) form update, (3) form add
    // if one of them not exist, generate the page individually
    for (const tbl of tables) {
      const existingPages = pageIndex.get(tbl.id)

      // standard details page
      if (!existingPages?.has('details')) {
        pagesToAdd.push(genDetailsPage(tbl, tables))
      }

      // form (update) details page
      if (!existingPages?.has('form-detail')) {
        pagesToAdd.push(genFormDetailsPage(tbl, tables))
      }

      // form (add) details page
      if (!existingPages?.has('form-add')) {
        pagesToAdd.push(genFormAddDetailsPage(tbl, tables))
      }
    }

    if (pagesToAdd.length > 0 || pagesToDelete.length > 0) {
      await handleGenerateDetailsPage(pagesToAdd, pagesToDelete)
    }

    // mark as processed
    isProcessingDetailsPageRef.current = false
    // ˝
  }, [appClient, handleGenerateDetailsPage, pages, tables])

  useEffect(() => {
    const handleAppPreviewLoaded = () => {
      setAppPreviewLoaded(true)
    }
    window.addEventListener(EVENT_KEYS.APP_PREVIEW_LOADED, handleAppPreviewLoaded)
    return () => window.removeEventListener(EVENT_KEYS.APP_PREVIEW_LOADED, handleAppPreviewLoaded)
  }, [])

  const pendingAutoSync =
    dbWorkerStatus !== 'READY' || !ENABLED || isProcessingTableActionRef.current || !appPreviewLoaded

  /**
   * Sync details page when dbWorker is ready
   */
  useEffect(() => {
    if (pendingAutoSync) return
    handleStartAutoSync()
  }, [handleStartAutoSync, pendingAutoSync])

  /**
   * Handle table events from database:
   * - DB_TABLE_CREATED: generate details pages for the new table
   * - DB_TABLE_DELETED: delete details pages of current table
   * - DB_TABLE_DUPLICATED: duplicate details pages of current table
   */
  useEffect(() => {
    if (!appClient || !dbClient) return

    const onFinish = () => {
      isProcessingTableActionRef.current = false
      window.dispatchEvent(new CustomEvent(EVENT_KEYS.PAGE_PROCESS_FINISHED))
    }

    /** @deprecated */
    // const handleTableDeleted = async (event: Event) => {
    //   const tableId = (event as CustomEvent).detail

    //   // get all pages that are using the table
    //   const appPages = Array.from(appClient.pagesMap.values())
    //   const pagesToDelete = appPages.filter((p) => p.settings._TableID === tableId)

    //   window.prettyLog(
    //     `[Builder] - delete ${pagesToDelete.length} detail pages`,
    //     pagesToDelete.map((p) => [p.settings._Settings._Title, p.settings._Nature]),
    //     '#840d56'
    //   )

    //   await handleGenerateDetailsPage(
    //     [],
    //     pagesToDelete.map((p) => p.settings._id)
    //   )

    //   onFinish()
    // }

    /** @deprecated */
    // const handleTableCreated = async (event: Event) => {
    //   if (isProcessingTableActionRef.current) return
    //   isProcessingTableActionRef.current = true

    //   const tableId = (event as CustomEvent).detail

    //   // check if the table is already in the pages
    //   const appPages = Array.from(appClient.pagesMap.values())
    //   if (appPages.some((page) => page.settings._TableID === tableId)) return onFinish()

    //   const newDBTable = dbClient.tablesMap.get(tableId)
    //   if (!newDBTable) return onFinish()

    //   const newTable = fromSdkTable(newDBTable.settings)
    //   const tables = fromSdkTableList(dbClient.GetTables().map((tb) => tb.settings) ?? [])

    //   if (tables.length === 0) return onFinish()

    //   const pagesToAdd = [
    //     // generate details page
    //     genDetailsPage(newTable, tables),
    //     // generate form details page
    //     genFormDetailsPage(newTable, tables),
    //   ]

    //   window.prettyLog(`[Builder] - creating ${pagesToAdd.length} detail pages`, pagesToAdd, '#840d56')
    //   await handleGenerateDetailsPage(pagesToAdd)

    //   onFinish()
    // }

    const handleTableDuplicated = async (event: Event) => {
      if (isProcessingTableActionRef.current) return
      isProcessingTableActionRef.current = true

      const { fromTableId, newTableId } = (event as CustomEvent).detail

      // find two pages that are using the table, duplicate them, and assign the tableID value
      const appPages = Array.from(appClient.pagesMap.values())
      const fromPages = appPages.filter((p) => p.settings._TableID === fromTableId)
      if (fromPages.length === 0) return onFinish()

      const pagesToDuplicate = fromPages.map((p) => {
        // replace the table id in the page settings
        const duplicatedPage = replaceTableIdsRecursive(
          fromTableId,
          newTableId,
          fromSdkPage(p.settings, DEFAULT_COMPONENTS_GROUPS)
        )
        return {
          ...duplicatedPage,
          title: duplicatedPage.nature === 'form' ? `${duplicatedPage.title}-copy` : duplicatedPage.title,
          id: uuid4(),
        } as BuilderPage
      })

      window.prettyLog(
        `[Builder] - ${pagesToDuplicate.length} pages duplicated`,
        {
          fromTableId,
          newTableId,
          pagesToDuplicate,
        },
        '#840d56'
      )
      await handleGenerateDetailsPage(pagesToDuplicate)

      onFinish()
    }

    const abortController = new AbortController()
    const signal = abortController.signal

    // window.addEventListener(EVENT_KEYS.DB_TABLE_CREATED, handleTableCreated, { signal })
    // window.addEventListener(EVENT_KEYS.DB_TABLE_DELETED, handleTableDeleted, { signal })
    window.addEventListener(EVENT_KEYS.DB_TABLE_DUPLICATED, handleTableDuplicated, { signal })

    return () => {
      abortController.abort()
    }
  }, [appClient, dbClient, handleGenerateDetailsPage])

  // MOCK
  const simulateSync = async (errorMode: 'none' | 'delete' | 'add' | 'both' = 'none') => {
    const PAGES_TO_ADD = 10
    const PAGES_TO_DELETE = 10

    setSyncState({
      status: 'processing',
      message: 'Simulating sync',
      deleteStatus: {
        current: 0,
        total: PAGES_TO_DELETE,
        status: 'idle',
        errors: 0,
      },
      addStatus: {
        current: 0,
        total: PAGES_TO_ADD,
        status: 'idle',
        errors: 0,
      },
      totalErrors: 0,
    })

    // Simulate deleting pages with sequential delays
    let deleteErrors = 0
    for (let i = 0; i < PAGES_TO_DELETE; i++) {
      await new Promise((resolve) => {
        setTimeout(() => {
          // Simulate error on 3rd and 7th item if errorMode includes delete
          const shouldError = (errorMode === 'delete' || errorMode === 'both') && (i === 2 || i === 6)

          if (shouldError) {
            deleteErrors++
          }

          const isLast = i + 1 === PAGES_TO_DELETE
          const status = shouldError ? 'error' : isLast ? 'finished' : 'processing'

          setSyncState((prev) => ({
            ...prev,
            deleteStatus: {
              current: i + 1,
              total: PAGES_TO_DELETE,
              status,
              errors: deleteErrors,
            },
          }))
          resolve(true)
        }, 100)
      })
    }

    // Simulate adding pages with sequential delays
    let addErrors = 0
    for (let i = 0; i < PAGES_TO_ADD; i++) {
      await new Promise((resolve) => {
        setTimeout(() => {
          // Simulate error on 4th and 8th item if errorMode includes add
          const shouldError = (errorMode === 'add' || errorMode === 'both') && (i === 3 || i === 7)

          if (shouldError) {
            addErrors++
          }

          const isLast = i + 1 === PAGES_TO_ADD
          const status = shouldError ? 'error' : isLast ? 'finished' : 'processing'

          setSyncState((prev) => ({
            ...prev,
            addStatus: {
              current: i + 1,
              total: PAGES_TO_ADD,
              status,
              errors: addErrors,
            },
          }))
          resolve(true)
        }, 100)
      })
    }

    // Final status update
    const totalErrors = deleteErrors + addErrors
    await new Promise((resolve) => {
      setTimeout(() => {
        setSyncState((prev) => ({
          ...prev,
          status: totalErrors > 0 ? 'error' : 'finished',
          message:
            totalErrors > 0
              ? `Completed with ${totalErrors} ${totalErrors === 1 ? 'error' : 'errors'}`
              : 'Sync completed successfully',
          totalErrors,
        }))
        resolve(true)
      }, 500)
    })
  }

  return {
    syncState,
    setSyncState,
    handleReset,
    simulateSync,
  }
}
